import React from 'react';
import { Box, Container, Typography, Stack, Button, Divider } from '@mui/material';
import Grid from '@mui/material/Grid';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import PeopleIcon from '@mui/icons-material/People';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import FavoriteIcon from '@mui/icons-material/Favorite';
import Header from './Header';
import Footer from './Footer';
import SEO from './SEO';
import { useNavigate, useLocation } from 'react-router-dom';
import { trackEvent } from './analytics';

const benefits = [
  {
    icon: <TrendingUpIcon sx={{ color: '#5ce1e6', fontSize: 24 }} />,
    title: 'Peak Hours Detection',
    body: "AI maps exactly when your rooms fill up and when coverage thins. Know your busiest occupancy windows before the week starts — not after you're already short-staffed.",
  },
  {
    icon: <PeopleIcon sx={{ color: '#5ce1e6', fontSize: 24 }} />,
    title: 'Staff Ratio Monitoring',
    body: "Real-time alerts when child-to-staff ratios approach your state's regulatory limits. No more manual headcounts, no more ratio violations caught by an inspector before you catch them yourself.",
  },
  {
    icon: <CalendarMonthIcon sx={{ color: '#5ce1e6', fontSize: 24 }} />,
    title: 'Smarter Scheduling',
    body: 'Historical pattern data turns gut-feel scheduling into data-driven decisions. Reduce overtime, eliminate coverage gaps, and stop rebuilding the same broken schedule week after week.',
  },
  {
    icon: <FavoriteIcon sx={{ color: '#5ce1e6', fontSize: 24 }} />,
    title: 'Worker Wellbeing Signals',
    body: 'When a normally calm worker shows behavioral drift over several days, that is one of the earliest signals of burnout. SMCLA flags it before it becomes a resignation — or an incident.',
  },
];

export default function ForStaffPage() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const handleBookDemo = () => { trackEvent('cta_click', 'Book a Demo', pathname); navigate('/contact'); };

  return (
    <Box sx={{ bgcolor: '#0a0a12', minHeight: '100vh' }}>
      <SEO title="For Daycare Staff & Operators — Smarter Scheduling & Safety" description="SMCLA's operational intelligence helps daycare operators schedule smarter, monitor staff ratios, and detect burnout before it becomes an incident." path="/for-staff" />
      <Header onBookDemo={handleBookDemo} />

      {/* ── Hero ── */}
      <Box sx={{ position: 'relative', overflow: 'hidden', bgcolor: '#0a0a12', pt: { xs: 10, md: 14 }, pb: { xs: 8, md: 12 }, px: 3, textAlign: 'center' }}>
        <Box sx={{ position: 'absolute', top: '-10%', left: '50%', transform: 'translateX(-50%)', width: 700, height: 500, borderRadius: '50%', background: 'radial-gradient(circle, rgba(92,225,230,0.12) 0%, transparent 70%)', filter: 'blur(60px)', pointerEvents: 'none' }} />
        <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1 }}>
          <Typography variant="overline" sx={{ display: 'block', color: '#5ce1e6', fontFamily: 'Inter, system-ui, sans-serif', fontSize: 11, letterSpacing: '0.12em', mb: 2 }}>
            Operational Intelligence
          </Typography>
          <Typography variant="h1" sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 800, fontSize: { xs: '2rem', sm: '3rem', md: '3.8rem' }, letterSpacing: '-0.03em', lineHeight: 1.05, color: '#f0f0f8', mb: 3 }}>
            Built for the people who<br />
            <Box component="span" sx={{ color: '#5ce1e6' }}>run childcare.</Box>
          </Typography>
          <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 400, fontSize: { xs: 16, md: 19 }, color: '#9499b0', maxWidth: 580, mx: 'auto', mb: 5, lineHeight: 1.65 }}>
            SMCLA doesn't just watch for problems. It learns your facility's patterns and tells you how to run it better — smarter staffing, fewer coverage gaps, and early signals before burnout or incident.
          </Typography>
          <Button
            variant="contained"
            size="large"
            onClick={() => { trackEvent('cta_click', 'See It In Action', pathname); navigate('/contact'); }}
            sx={{ px: 4, py: 1.5, fontWeight: 700, fontSize: 16, bgcolor: '#5ce1e6', color: '#0a0a12', borderRadius: 2, boxShadow: '0 0 24px rgba(92,225,230,0.2)', '&:hover': { bgcolor: '#4ecfd4' } }}
          >
            See It In Action →
          </Button>

          {/* Hero image */}
          <Box sx={{ mt: 7, borderRadius: 3, overflow: 'hidden', border: '1px solid rgba(92,225,230,0.18)', boxShadow: '0 0 60px rgba(92,225,230,0.08)' }}>
            <Box
              component="img"
              src="/ai-people-tech.png"
              alt="Daycare worker with children and AI operational dashboard"
              sx={{ width: '100%', display: 'block', objectFit: 'cover', maxHeight: 420 }}
            />
          </Box>
        </Container>
      </Box>

      {/* ── 4 Benefit Cards ── */}
      <Box sx={{ bgcolor: '#0d0d18', borderTop: '1px solid rgba(255,255,255,0.06)', borderBottom: '1px solid rgba(255,255,255,0.06)', py: { xs: 8, md: 12 }, px: 3 }}>
        <Container maxWidth="md">
          <Typography variant="overline" sx={{ display: 'block', textAlign: 'center', color: '#5ce1e6', fontFamily: 'Inter, system-ui, sans-serif', fontSize: 11, letterSpacing: '0.12em', mb: 1.5 }}>
            What You Get
          </Typography>
          <Typography variant="h3" sx={{ textAlign: 'center', fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 800, fontSize: { xs: '1.8rem', md: '2.5rem' }, color: '#f0f0f8', mb: 1.5, letterSpacing: '-0.02em' }}>
            Safety data that runs your operation
          </Typography>
          <Typography sx={{ textAlign: 'center', color: '#9499b0', fontFamily: 'Inter, system-ui, sans-serif', fontSize: 16, mb: 7, maxWidth: 520, mx: 'auto', lineHeight: 1.65 }}>
            The same sensors that detect abuse also map your occupancy, staffing ratios, and worker behavior over time. That data belongs to you.
          </Typography>
          <Grid container spacing={3}>
            {benefits.map(({ icon, title, body }) => (
              <Grid key={title} size={{ xs: 12, md: 6 }}>
                <Box sx={{ bgcolor: '#13131f', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 3, p: 3.5, height: '100%', transition: 'all 0.2s ease', '&:hover': { borderColor: 'rgba(92,225,230,0.3)', transform: 'translateY(-4px)', boxShadow: '0 20px 40px rgba(92,225,230,0.07)' } }}>
                  <Box sx={{ display: 'inline-flex', p: 1.25, bgcolor: 'rgba(92,225,230,0.08)', borderRadius: 2, mb: 2.5 }}>
                    {icon}
                  </Box>
                  <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 700, fontSize: 17, color: '#f0f0f8', mb: 1.25 }}>
                    {title}
                  </Typography>
                  <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 14, color: '#9499b0', lineHeight: 1.7 }}>
                    {body}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ── How it actually works ── */}
      <Box sx={{ py: { xs: 8, md: 12 }, px: 3 }}>
        <Container maxWidth="md">
          <Typography variant="overline" sx={{ display: 'block', textAlign: 'center', color: '#5ce1e6', fontFamily: 'Inter, system-ui, sans-serif', fontSize: 11, letterSpacing: '0.12em', mb: 1.5 }}>
            Under the Hood
          </Typography>
          <Typography variant="h3" sx={{ textAlign: 'center', fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 800, fontSize: { xs: '1.8rem', md: '2.5rem' }, color: '#f0f0f8', mb: 1.5, letterSpacing: '-0.02em' }}>
            One system. Two outcomes.
          </Typography>
          <Typography sx={{ textAlign: 'center', color: '#9499b0', fontFamily: 'Inter, system-ui, sans-serif', fontSize: 16, mb: 7, maxWidth: 520, mx: 'auto', lineHeight: 1.65 }}>
            SMCLA runs continuous behavioral analysis across every camera in your facility. Safety alerts and operational insights come from the same feed — no extra sensors, no extra subscriptions.
          </Typography>
          <Grid container spacing={3}>
            {[
              {
                label: 'Safety layer',
                color: '#ff6b6b',
                points: [
                  'Physical abuse detection — real-time alerts within 60 seconds',
                  'Audio / emotional abuse — threatening language, raised voices, verbal intimidation',
                  'Behavioral anomalies — unusual worker proximity, isolation patterns',
                  'Tamper-evident incident clips — legal-grade documentation',
                ],
              },
              {
                label: 'Operations layer',
                color: '#5ce1e6',
                points: [
                  'Room-level occupancy tracking — peak windows mapped automatically',
                  'Child-to-staff ratio monitoring — alert before a violation, not after',
                  'Historical scheduling data — see which shifts are chronically understaffed',
                  'Worker pattern baselines — detect burnout drift days before resignation',
                ],
              },
            ].map(({ label, color, points }) => (
              <Grid key={label} size={{ xs: 12, md: 6 }}>
                <Box sx={{ p: 3.5, bgcolor: '#13131f', border: `1px solid ${color}20`, borderTop: `3px solid ${color}`, borderRadius: 2, height: '100%' }}>
                  <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 700, fontSize: 12, color, mb: 2.5, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                    {label}
                  </Typography>
                  <Stack spacing={1.5}>
                    {points.map((pt, i) => (
                      <Box key={i} sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                        <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: color, mt: 0.7, flexShrink: 0 }} />
                        <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 13, color: '#9499b0', lineHeight: 1.6 }}>{pt}</Typography>
                      </Box>
                    ))}
                  </Stack>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ── Quote ── */}
      <Box sx={{ py: { xs: 8, md: 12 }, px: 3, textAlign: 'center', position: 'relative', overflow: 'hidden' }}>
        <Typography sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -55%)', fontFamily: 'Georgia, serif', fontSize: { xs: 200, md: 280 }, lineHeight: 1, color: 'rgba(92,225,230,0.03)', pointerEvents: 'none', userSelect: 'none' }}>"</Typography>
        <Container maxWidth="sm" sx={{ position: 'relative', zIndex: 1 }}>
          <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 500, fontStyle: 'italic', fontSize: { xs: 19, md: 24 }, color: '#d0d0e0', lineHeight: 1.55, mb: 3 }}>
            "Our director used to spend Sunday nights guessing at the schedule. Now she pulls the pattern report and has it built in 20 minutes."
          </Typography>
          <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 500, fontSize: 13, color: '#9499b0', letterSpacing: '0.04em' }}>
            — Multi-site operator, pilot program
          </Typography>
        </Container>
      </Box>

      {/* ── What operators tell us ── */}
      <Box sx={{ bgcolor: '#0d0d18', borderTop: '1px solid rgba(255,255,255,0.06)', borderBottom: '1px solid rgba(255,255,255,0.06)', py: { xs: 8, md: 12 }, px: 3 }}>
        <Container maxWidth="md">
          <Typography variant="overline" sx={{ display: 'block', textAlign: 'center', color: '#5ce1e6', fontFamily: 'Inter, system-ui, sans-serif', fontSize: 11, letterSpacing: '0.12em', mb: 1.5 }}>
            Designed With Operators
          </Typography>
          <Typography variant="h3" sx={{ textAlign: 'center', fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 800, fontSize: { xs: '1.8rem', md: '2.5rem' }, color: '#f0f0f8', mb: 1.5, letterSpacing: '-0.02em' }}>
            The problems operators actually have
          </Typography>
          <Typography sx={{ textAlign: 'center', color: '#9499b0', fontFamily: 'Inter, system-ui, sans-serif', fontSize: 16, mb: 7, maxWidth: 520, mx: 'auto' }}>
            These aren't hypotheticals. They're the operational problems that came up in every pilot conversation.
          </Typography>
          <Grid container spacing={2.5}>
            {[
              {
                problem: '"I never know which shifts are going to be short until they already are."',
                solution: 'Peak hours detection shows your recurring coverage gaps before they happen. Historical occupancy data becomes your scheduling calendar.',
              },
              {
                problem: '"A great worker quit and I had no idea she was struggling until she handed in her notice."',
                solution: 'Behavioral baseline drift detection surfaces workers whose patterns change significantly over days — a leading indicator of burnout and disengagement.',
              },
              {
                problem: "\"We got cited for a ratio violation. I didn't even know we were close.\"",
                solution: 'Real-time ratio monitoring fires an alert when your child-to-staff count approaches the regulatory limit — not after you cross it.',
              },
              {
                problem: "\"I have three locations. I can't be in all of them. Something always slips.\"",
                solution: 'One dashboard. All rooms, all locations. Operational anomalies surface automatically — you see everything without being everywhere.',
              },
            ].map(({ problem, solution }) => (
              <Grid key={problem} size={{ xs: 12, md: 6 }}>
                <Box sx={{ p: 3, bgcolor: '#13131f', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 2.5, height: '100%' }}>
                  <Typography sx={{ fontFamily: '"Libre Baskerville", Georgia, serif', fontStyle: 'italic', fontSize: 14, color: '#d0d0e0', lineHeight: 1.65, mb: 2, borderLeft: '3px solid rgba(92,225,230,0.3)', pl: 2 }}>
                    {problem}
                  </Typography>
                  <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 13, color: '#9499b0', lineHeight: 1.65 }}>
                    {solution}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ── CTA ── */}
      <Box sx={{ py: { xs: 8, md: 12 }, px: 3 }}>
        <Container maxWidth="sm" sx={{ textAlign: 'center' }}>
          <Typography variant="h3" sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 800, fontSize: { xs: '1.8rem', md: '2.4rem' }, color: '#f0f0f8', letterSpacing: '-0.02em', mb: 2 }}>
            See how SMCLA fits your operation
          </Typography>
          <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 16, color: '#9499b0', mb: 4, lineHeight: 1.65 }}>
            A 30-minute walkthrough tailored to your facility size, room count, and staffing challenges. We show you the safety layer and the operations layer — both in one session.
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
            <Button variant="contained" size="large" onClick={handleBookDemo} sx={{ px: 4, py: 1.5, fontWeight: 700, fontSize: 15, bgcolor: '#5ce1e6', color: '#0a0a12', borderRadius: 2, '&:hover': { bgcolor: '#4ecfd4' } }}>
              Book a Demo →
            </Button>
            <Button variant="outlined" size="large" onClick={() => navigate('/enterprise')} sx={{ px: 4, py: 1.5, fontWeight: 600, fontSize: 15, color: '#f0f0f8', borderColor: 'rgba(255,255,255,0.2)', borderRadius: 2, '&:hover': { borderColor: 'rgba(255,255,255,0.4)', bgcolor: 'rgba(255,255,255,0.05)' } }}>
              Enterprise Options →
            </Button>
          </Stack>
        </Container>
      </Box>

      <Footer />
    </Box>
  );
}
